import { IconPlus } from '@tabler/icons-react';
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import Modal from '../ogComponent/modal';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';

export default function CourseList() {
    const toast = useRef(null);
    const [showModal, setShowModal] = useState(false);
    const [subCourses, setSubCourses] = useState([]);
    const [Courses, setCourses] = useState([]);
    const navigate = useNavigate();

    // Access permissions array from Redux store
    const permissions = useSelector((state) => state.emp.permissions);

    const pagePermissions = ["addcourseID", "showcourseID"];

    const courseFields = [
        { name: 'course_name', label: 'Course Name', type: 'text', required: true },
        { name: 'course_duration', label: 'Course Duration (days)', type: 'number', required: true },
        { name: 'course_fee', label: 'Course Fee', type: 'number', required: true },
        { name: 'description', label: 'Description', type: 'textarea', required: true },
    ];

    const fetchSubCourses = async () => {
        const token = localStorage.getItem('accessToken');
        try {
            const response = await fetch(`${process.env.REACT_APP_API}/api/course/individual-courses/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                const formattedSubCourses = data.map((course) => ({
                    id: course.id,
                    label: course.title,
                    value: course.id,
                }));
                setSubCourses(formattedSubCourses);
            } else {
                console.error("Failed to fetch sub-courses:", response.statusText);
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch sub-courses',
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error fetching sub-courses:", error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch sub-courses',
                life: 3000,
            });
        }
    };

    const fetchCourses = async () => {
        const token = localStorage.getItem('accessToken');
        try {
            const response = await fetch(`${process.env.REACT_APP_API}/api/course/combined-courses/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setCourses(data);
            } else {
                console.error("Failed to fetch courses:", response.statusText);
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch courses',
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error fetching courses:", error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch courses',
                life: 3000,
            });
        }
    };

    useEffect(() => {
        if (permissions.includes("showcourseID")) {
            fetchCourses();
        }
        fetchSubCourses();
    }, [permissions]);

    const handleAddCourse = () => {
        setShowModal(true);
    };

    const handleCourseClick = (id) => {
        navigate(`/user/courses/${id}`);
    };

    const handleCourseSubmit = async (formData) => {
        const transformedData = {
            course_name: formData.course_name,
            description: formData.description,
            sub_courses: formData.sub_courses?.map((course) => course.id) || [],
            duration: formData.course_duration,
            fee: formData.course_fee,
        };

        const token = localStorage.getItem('accessToken');

        try {
            const response = await fetch(`${process.env.REACT_APP_API}/api/course/combined-courses/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(transformedData),
            });

            if (response.ok) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: "New Course Created!",
                    life: 3000,
                });
                setShowModal(false);
                if (permissions.includes("showcourseID")) {
                    fetchCourses();
                }
            } else {
                console.error("Failed to add course:", response.statusText);
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: "Failed to Create Course",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error submitting course data:", error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: "Failed to Create Course",
                life: 3000,
            });
        }
    };

    useEffect(() => {
        if (showModal) {
            const modalElement = document.getElementById('staticBackdrop');
            const modalInstance = new window.bootstrap.Modal(modalElement);
            modalInstance.show();

            modalElement.addEventListener('hidden.bs.modal', () => setShowModal(false), { once: true });
        }
    }, [showModal]);

    return (
        <div className="container-fluid px-3 px-md-4 d-flex flex-column gap-3">
            <Toast ref={toast} position="bottom-right" />
            <div className="d-flex flex-column justify-content-start mt-3 mb-1">
                <h5 className="fw-light mb-0">Courses</h5>
                <p
                    className="align-self-start mb-0"
                    style={{ color: "grey", fontSize: "clamp(10px, 1.1vw, 12px)", fontWeight: '600' }}
                >
                    Total {Courses.length} Course{Courses.length !== 1 ? "s" : ""}
                </p>
            </div>
            {permissions.includes("addcourseID") && (
                <button className="btn btn-success" style={{ width: '150px' }} onClick={handleAddCourse}>
                    Add New <IconPlus stroke={2} />
                </button>
            )}
            {permissions.includes("showcourseID") ? (
                Courses.length === 0 ? (
                    <div
                        className="d-flex justify-content-center align-items-end position-relative"
                        style={{ height: "50vh" }}
                    >
                        <div
                            className="position-absolute top-0 start-0 w-100 h-100"
                            style={{
                                backgroundImage: 'url(/assets/file.png)',
                                backgroundSize: 'contain',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                opacity: 0.3,
                            }}
                        ></div>
                        <p className="m-0 fw-light fs-5 position-relative">No Courses!</p>
                    </div>
                ) : (
                    <div className="row">
                        {Courses.map((course) => (
                            <div
                                key={course.id}
                                className="col-12 col-sm-6 col-md-6 col-lg-4 mb-3"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleCourseClick(course.id)}
                            >
                                <div className="card h-100 px-3 pb-2">
                                    <div className="card-body d-flex flex-column py-1 px-0">
                                        <div className="p-2">
                                            <h6
                                                className="card-title mb-0"
                                                style={{
                                                    fontSize: 'clamp(12px, 1.2vw, 14px)',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    width: '100%'
                                                }}
                                                title={course?.course_name}
                                            >
                                                {course?.course_name}
                                            </h6>
                                        </div>
                                        <div
                                            className="rounded-3 px-3 py-2"
                                            style={{ backgroundColor: "#f8f7f7", fontSize: 'clamp(12px, 1.2vw, 14px)' }}
                                        >
                                            <p className="card-text" style={{ marginBottom: '0.5rem' }}>
                                                <i className="bi bi-box-seam me-2"></i>
                                                Duration: {course.duration} days
                                            </p>
                                            <p className="card-text" style={{ marginBottom: '0.5rem' }}>
                                                <i className="bi bi-people me-2"></i>
                                                Description: {course.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            ) : (
                <p>No permission to view courses.</p>
            )}

            {permissions.includes("addcourseID") && (
                <Modal
                    heading="Add Course"
                    fields={courseFields}
                    onSubmit={handleCourseSubmit}
                    type={"course"}
                />
            )}
        </div>
    );
}